import Button from "@app/components/button";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
} from "@app/constants";
import LogService from "@app/services/http/log.service";
import { Meta } from "@app/types";
import { ActionLog } from "@app/types/log.type";
import { formatDateTime } from "@app/utils/util";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { useEffect, useState } from "react";

function CreatedDateColumnTable({ data }: { data: ActionLog }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

function TargetUrlDataColumnTable({ data }: { data: ActionLog }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(data.targetUrl).then(() => {
      addToast({ text: "Coppy thành công" });
    });
  };

  return (
    <div
      className="truncate max-w-[500px] cursor-pointer"
      onClick={handleCopy}
      title="Click để sao chép"
    >
      {data.targetUrl.slice(0, 100)}...
    </div>
  );
}

function TypeDataColumnTable({ data }: { data: ActionLog }) {
  let className = "";

  switch (data.type) {
    case "UPDATE_BOOK":
      className = "text-[#4558FF] bg-[#EBF5FF]";
      break;
    case "DELETE_BOOK":
      className = "text-[#FC4040] bg-[#FFEBEB]";
      break;
    case "UPDATE_SETTING":
      className = "text-[#FF7029] bg-[#FFF0E6]";
      break;
    default:
      className = "text-gray-500";
  }

  return (
    <div className="truncate max-w-[500px]">
      <div
        className={clsx(
          "py-[8px] px-[16px] rounded-[8px] inline-block text-[12px]",
          className
        )}
      >
        {data.type}
      </div>
    </div>
  );
}

function ActionColumnTable({ data }: { data: ActionLog }) {
  return (
    <div className="truncate max-w-[200px]">
      <Button
        label="Restore"
        // disabled={!data.isRestored}
        onClick={() => {
          LogService.restore(data.id).then(() => {
            addToast({ text: "Khôi phục thành công" });
          });
        }}
      />
    </div>
  );
}

function LogManagement() {
  const [actionsLog, setActionsLog] = useState<ActionLog[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    LogService.getList(requestParams, abortController.signal).then((res) => {
      setActionsLog(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [requestParams]);

  const columnConfig: ColumnConfig[] = [
    {
      label: "Ngày tạo",
      dataKey: "createdDate",
      width: "20%",
      component: CreatedDateColumnTable,
      hasOrder: true,
    },
    {
      label: "Tên đăng nhập",
      dataKey: "username",
      width: "20%",
      hasOrder: true,
    },
    {
      label: "Target URL",
      dataKey: "targetUrl",
      width: "20%",
      component: TargetUrlDataColumnTable,
      hasOrder: true,
    },
    {
      label: "Type",
      dataKey: "type",
      width: "20%",
      component: TypeDataColumnTable,
      hasOrder: true,
    },
    {
      label: "Hành động",
      dataKey: "isRestored",
      width: "20%",
      component: ActionColumnTable,
      hasOrder: true,
    },
  ];

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách Logs</div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách Logs"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={actionsLog}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default LogManagement;
